import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Form, message, Upload, Spin, Tooltip } from 'antd'
import { useDebounceFn, useMount, useRequest } from 'ahooks'
import classNames from 'classnames'
import styled from '@emotion/styled'
import { useWatch } from 'antd/es/form/Form'
import type { TextAreaRef } from 'antd/es/input/TextArea'

import { Modal, Button, TextArea, IconFont } from '@/components'
import type { ParagraphItem } from '../ParagraphListItem'
import {
  addSplitContent,
  updateSplitContent,
  fetchTokenSizeByWord,
} from '@/apis/datastore/api'
import type { Paragraph } from '@/pages/datastores/types/paragraph'
import KeywordsEditor from '@/pages/datastores/components/KeywordsEditor.tsx'

// import { MAX_SPLIT_SIZE } from '@/features/datastore/constant'
import { ParagraphExtraInfoStatus } from '@/apis/datastore/model'
import { useOSSClient } from '@/hooks/useOSSClient'
import { checkFileSize } from '@/features/datastore/utils'

const TEMP_MAX_SPLIT_SIZE = 20000

export function RequireIcon() {
  return (
    <span className='color-error ml-5 text-[18px] line-height-14px position-relative top-[3px]'>
      *
    </span>
  )
}

export interface NormalParagraphEditorModalProps {
  title?: string | React.ReactElement
  defaultValue?: string[]
  open?: boolean
  onCancel?: () => void
  onFinish?: () => void
  paragraph: Omit<ParagraphItem, 'key'> & {
    key?: ParagraphItem['key']
  }
  file_id: number
  chunk_id?: number
  insert_above_chunk_id?: number
  callback: (value: Paragraph) => void
  matched_keywords?: string[]
  operationType: 'update' | 'add'
}

const FormWrapper = styled(Form)`
  .ant-form-item-required {
    ::before {
      display: none !important;
    }
  }
`

const TextAreaWrapper = styled(TextArea)`
  height: 45vh;
  overflow-y: auto;
  .ant-input {
    color: #17171d;
  }
  .ant-input-data-count {
    display: none;
  }

  .ant-input {
    padding-bottom: 0px !important;
    border-bottom: 36px solid #fff !important;
  }
`

function TextAreaWrapperWithToken(props: {
  usedToken?: number
  maxToken?: number
  value?: any
  onChange?: (value: any) => void
  onBlur?: () => void
  onImageUpload?: (file: any) => void
}) {
  const textareaRef = useRef<TextAreaRef>(null)
  const [loading, setLoading] = useState(false)
  /**
   * 在文本字段内的光标位置插入文本值。
   * @param myField HTMLInputElement | HTMLTextAreaElement - 目标字段。
   * @param myValue string - 要插入的值。
   * @param onChange (value: string) => void - 输入值改变时的回调函数。
   */
  const insertAtCursor = (
    myField: HTMLInputElement | HTMLTextAreaElement | undefined,
    myValue?: string,
  ): void => {
    if (!myField || !myValue) return

    // 现代浏览器支持
    if (myField.selectionStart !== undefined || myField.selectionStart === 0) {
      const startPos = myField.selectionStart ?? 0
      const endPos = myField.selectionEnd ?? 0
      myField.value =
        myField.value.substring(0, startPos) +
        myValue +
        myField.value.substring(endPos, myField.value.length)
      const newPos = startPos + myValue.length
      myField.selectionStart = newPos
      myField.selectionEnd = newPos
    } else {
      // 不支持 selectionStart 的浏览器
      myField.value += myValue
    }
    myField?.focus()
    props?.onChange?.(myField.value)
  }

  const handleRequest = async (options: any) => {
    const { file } = options
    try {
      setLoading(true)
      const url = await props?.onImageUpload?.(file)
      // 插入图片的markdown形式
      const insertValue = `\n![${file.name}](${url})\n`
      insertAtCursor(
        textareaRef?.current?.resizableTextArea?.textArea,
        insertValue,
      )
    } catch (e) {
      message.error('图片上传失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  const beforeUpload = (file: File): any => {
    if (!checkFileSize(file.size, 20)) {
      message.warning('图片大小不能超过20MB, 请修改后重新上传')
      return Upload.LIST_IGNORE
    }
    return true
  }

  const uploadProps = {
    customRequest: handleRequest,
    accept: 'image/*',
    maxCount: 1,
    itemRender: () => null,
    showUploadList: false,
    beforeUpload,
  }

  return (
    <div className='relative'>
      <div className='relative'>
        <Spin spinning={loading}>
          <TextAreaWrapper
            className={classNames('w-full bg-white', {
              '!b-0 !hover:b-0 !shadow-none': false,
            })}
            autoFocus
            ref={textareaRef}
            value={props?.value}
            onChange={props?.onChange}
            onBlur={props?.onBlur}
            placeholder={'输入内容'}
            readOnly={false}
            count={{
              show: true,
              max: TEMP_MAX_SPLIT_SIZE,
            }}
          />
          <Upload
            {...uploadProps}
            className='absolute bottom-[8px] text-20px w-24px h-24px left-8px z-100 cursor-pointer hover:bg-#62696D hover:bg-op-8 rounded-4px flex items-center justify-center'
          >
            <Tooltip title='上传图片'>
              <IconFont name='shangzhuantupian' className='c-#B9B9CD' />
            </Tooltip>
          </Upload>
        </Spin>
      </div>
      {!!props?.usedToken && (
        <div className='mt-12px text-12px/12px c-#8D8D99 flex items-center'>
          <IconFont name='zifushu' />
          <p className='ml-4px'>
            <span
              className={classNames({
                'c-error': props?.usedToken > (props?.maxToken || 0),
              })}
            >
              {props?.usedToken}
            </span>
            /{props?.maxToken}
          </p>
        </div>
      )}
    </div>
  )
}

export const NormalParagraphEditorModal: FC<
  NormalParagraphEditorModalProps
> = props => {
  const {
    open,
    onCancel: onModalClose,
    title = '编辑段落',
    paragraph,
    file_id,
    chunk_id,
    callback,
    matched_keywords = [],
    operationType,
    insert_above_chunk_id,
  } = props

  const { ossClient } = useOSSClient({ business: 'dataset' })

  const onImageUpload = async (file: File) => {
    if (ossClient) {
      const filePath = `i/datastore_normal/${file_id}/${+new Date()}/${
        file.name
      }`

      ossClient.useBucket(import.meta.env.VITE_AI_DATASTORE_RESOURCE_BUCKET)
      const res = await ossClient.put(filePath, file)

      return res.url
    }
    throw new Error('oss client is not init')
  }

  const [wordTokenInfo, setWordTokenInfo] = useState<{
    usedToken: number
    maxToken: number
  }>({
    usedToken: 0,
    maxToken: 0,
  })
  const [form] = Form.useForm()
  const [keywords, setKeyWords] = useState(paragraph.keywords ?? [])

  const { runAsync, loading } = useRequest(updateSplitContent, { manual: true })
  const { runAsync: runAddSplitItem, loading: addLoading } = useRequest(
    addSplitContent,
    { manual: true, debounceWait: 300 },
  )

  const { run: getWorkSize, flush: getWorkSizeImmediately } = useDebounceFn(
    async (showToast = false) => {
      const content = form?.getFieldValue('content') || ''
      const data = await fetchTokenSizeByWord({ file_id, content })
      setWordTokenInfo({
        usedToken: data.token_consumption,
        maxToken: data.max_token,
      })
      if (data.token_consumption > data.max_token) {
        if (showToast) {
          message.error('字符超出限制')
        }
        throw new Error('字符超出限制')
      }
    },
    {
      wait: 300,
    },
  )
  const formContent = useWatch(['content'], form)

  useEffect(() => {
    getWorkSize()
  }, [formContent])

  useMount(() => {
    getWorkSizeImmediately()
  })

  const onOk = async () => {
    await getWorkSize(true)
    await form.validateFields()
    const pre_chunk_status = paragraph?.extra_info?.chunk_status
    const baseParams = {
      file_id,
      ...form.getFieldsValue(),
      keywords,
      extra_info: {
        ...paragraph.extra_info,
        chunk_status: ParagraphExtraInfoStatus.Done,
      },
    }

    if (operationType === 'update') {
      const updateParams = {
        chunk_id,
        enable:
          pre_chunk_status === ParagraphExtraInfoStatus.Error
            ? true
            : paragraph.enable,
      }
      await runAsync({ ...baseParams, ...updateParams })
      callback?.({ ...baseParams, ...updateParams })
    } else {
      const addParams = {
        enable: true,
        insert_above_chunk_id,
      }
      const res = await runAddSplitItem({ ...baseParams, ...addParams })
      callback?.({ ...res, key: res.chunk_id })
    }
    message.success(operationType === 'update' ? '修改已保存' : '添加成功')
    onModalClose?.()
  }

  return (
    <Modal
      title={title}
      open={open}
      maskClosable={false}
      onCancel={onModalClose}
      footer={null}
      width={1020}
      styles={{
        body: {
          minHeight: '420px',
          height: '67vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px',
          padding: '24px 32px',
        },
      }}
    >
      <div
        className={classNames({
          '!pt-0': operationType !== 'update',
        })}
      >
        {
          <div className='flex flex-wrap gap-8'>
            <KeywordsEditor
              defaultValue={keywords}
              readOnly={false}
              highlightKeywords={matched_keywords}
              onChange={setKeyWords}
            />
          </div>
        }
        <div className='pt-[24px] pb-[24px]'>
          <FormWrapper
            form={form}
            layout={'vertical'}
            initialValues={{
              content: paragraph?.content || '',
              extra_info: paragraph?.extra_info,
            }}
          >
            <Form.Item
              name='content'
              rules={[{ required: true, message: '请输入内容' }]}
            >
              <TextAreaWrapperWithToken
                usedToken={wordTokenInfo.usedToken}
                maxToken={wordTokenInfo.maxToken}
                onImageUpload={onImageUpload}
              />
            </Form.Item>
          </FormWrapper>
        </div>
      </div>
      <div className='px-32 pb-24 flex items-center justify-end gap-12 absolute bottom-0 w-full left-0 bg-white rounded-[0_0_8px_8px] z-100'>
        <Button
          onClick={() => {
            form.resetFields()
            onModalClose?.()
          }}
        >
          取消
        </Button>
        <Button
          loading={operationType === 'update' ? loading : addLoading}
          type='primary'
          onClick={onOk}
        >
          保存
        </Button>
      </div>
    </Modal>
  )
}
